import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useReports } from '../reports/ReportsContext';
import { useAccess, PERMISSIONS } from '../auth/AccessContext';

// Groups the flat report catalog into { category: [reports] }, preserving the
// order the server returned them in.
function groupByCategory(reports) {
    const groups = [];
    const index = {};
    for (const r of reports) {
        if (!(r.category in index)) {
            index[r.category] = groups.length;
            groups.push({ category: r.category, reports: [] });
        }
        groups[index[r.category]].reports.push(r);
    }
    return groups;
}

// The Marketing section's pages. Unlike Reports (which the server's catalog
// drives), these are fixed read/write admin screens, so the list lives here —
// each with the permission that reveals it.
const MARKETING_PAGES = [
    { to: '/marketing/vendors', label: 'Vendors', description: 'Add, rename, and remove marketing vendors', permission: PERMISSIONS.marketingVendors },
    { to: '/marketing/campaigns', label: 'Campaigns', description: 'Campaigns by vendor, with source and type', permission: PERMISSIONS.marketingCampaigns },
    { to: '/marketing/spends', label: 'Spends', description: 'Enter spend across a date range', permission: PERMISSIONS.marketingSpends },
    { to: '/marketing/ad-sync', label: 'Ad Spend Sync', description: 'Pull daily spend from Google Ads and Meta', permission: PERMISSIONS.marketingAdSync },
];

// The Commissions section's pages, all under the one section permission.
const COMMISSION_PAGES = [
    { to: '/commissions/splits', label: 'Splits', description: 'Each case\'s split agents; the settlement officer gets the rest' },
    { to: '/commissions/agents', label: 'Agents', description: 'Split agents and settlement officers, with or without an app login' },
    { to: '/commissions/imports', label: 'Payment Imports', description: 'Upload the IRS Logics payment report' },
    { to: '/commissions/earned', label: 'Earned', description: 'Commission earned by agent over a date range' },
];

// User management's two pages, shown only to admin profiles.
const ADMIN_PAGES = [
    { to: '/users', label: 'Users', description: 'Add people and assign their profile' },
    { to: '/profiles', label: 'Profiles', description: 'Edit what each profile can see' },
];

export default function Sidebar({ onNavigate }) {
    const { reports, loading, error } = useReports();
    const { can } = useAccess();
    const location = useLocation();
    const onReportsRoute = location.pathname.startsWith('/reports');
    const onMarketingRoute = location.pathname.startsWith('/marketing');
    const onAdminRoute = location.pathname.startsWith('/users') || location.pathname.startsWith('/profiles');
    const onCommissionsRoute = location.pathname.startsWith('/commissions');

    // What this profile may see. Sections whose pages are all off stay hidden
    // rather than opening onto an empty submenu.
    const marketingPages = MARKETING_PAGES.filter(page => can(page.permission));
    const showMarketing = can(PERMISSIONS.marketing) && marketingPages.length > 0;
    const showReports = can(PERMISSIONS.reports);
    const showAddendum = can(PERMISSIONS.addendum);
    const showAdmin = can(PERMISSIONS.users);
    const showBlocked = can(PERMISSIONS.blocked);
    const showCanopy = can(PERMISSIONS.canopy);
    const showCommissions = can(PERMISSIONS.commissions);

    // Reports submenu starts open when we're already on a report route.
    const [reportsOpen, setReportsOpen] = useState(onReportsRoute);
    useEffect(() => {
        if (onReportsRoute) setReportsOpen(true);
    }, [onReportsRoute]);

    const [marketingOpen, setMarketingOpen] = useState(onMarketingRoute);
    useEffect(() => {
        if (onMarketingRoute) setMarketingOpen(true);
    }, [onMarketingRoute]);

    const [commissionsOpen, setCommissionsOpen] = useState(onCommissionsRoute);
    useEffect(() => {
        if (onCommissionsRoute) setCommissionsOpen(true);
    }, [onCommissionsRoute]);

    const [adminOpen, setAdminOpen] = useState(onAdminRoute);
    useEffect(() => {
        if (onAdminRoute) setAdminOpen(true);
    }, [onAdminRoute]);

    const groups = groupByCategory(reports);

    return (
        <nav className="sidebar__nav" aria-label="Primary">
            {showAddendum && (
                <NavLink
                    to="/addendum"
                    className={({ isActive }) => `sidebar__item ${isActive ? 'sidebar__item--active' : ''}`}
                    onClick={onNavigate}
                >
                    <span className="sidebar__icon" aria-hidden="true">📄</span>
                    Addendum Generator
                </NavLink>
            )}

            {showMarketing && (<>
            <button
                type="button"
                className={`sidebar__item sidebar__parent ${onMarketingRoute ? 'sidebar__item--active' : ''}`}
                onClick={() => setMarketingOpen(open => !open)}
                aria-expanded={marketingOpen}
            >
                <span className="sidebar__icon" aria-hidden="true">🎯</span>
                Marketing
                <span className={`sidebar__chevron ${marketingOpen ? 'sidebar__chevron--open' : ''}`}>▸</span>
            </button>

            {marketingOpen && (
                <div className="sidebar__submenu">
                    {marketingPages.map(page => (
                        <NavLink
                            key={page.to}
                            to={page.to}
                            className={({ isActive }) => `sidebar__subitem ${isActive ? 'sidebar__subitem--active' : ''}`}
                            title={page.description}
                            onClick={onNavigate}
                        >
                            {page.label}
                        </NavLink>
                    ))}
                </div>
            )}
            </>)}

            {showReports && (<>
            <button
                type="button"
                className={`sidebar__item sidebar__parent ${onReportsRoute ? 'sidebar__item--active' : ''}`}
                onClick={() => setReportsOpen(open => !open)}
                aria-expanded={reportsOpen}
            >
                <span className="sidebar__icon" aria-hidden="true">📊</span>
                Reports
                <span className={`sidebar__chevron ${reportsOpen ? 'sidebar__chevron--open' : ''}`}>▸</span>
            </button>

            {reportsOpen && (
                <div className="sidebar__submenu">
                    {loading && <p className="sidebar__hint">Loading reports…</p>}
                    {error && <p className="sidebar__hint sidebar__hint--error">{error}</p>}
                    {!loading && !error && reports.length === 0 && (
                        <p className="sidebar__hint">No reports configured</p>
                    )}
                    {groups.map(group => (
                        <div key={group.category} className="sidebar__group">
                            <p className="sidebar__group-label">{group.category}</p>
                            {group.reports.map(report => (
                                <NavLink
                                    key={report.key}
                                    to={`/reports/${report.key}`}
                                    className={({ isActive }) => `sidebar__subitem ${isActive ? 'sidebar__subitem--active' : ''}`}
                                    title={report.description}
                                    onClick={onNavigate}
                                >
                                    {report.label}
                                </NavLink>
                            ))}
                        </div>
                    ))}
                </div>
            )}
            </>)}

            {showBlocked && (
                <NavLink
                    to="/blocked"
                    className={({ isActive }) => `sidebar__item ${isActive ? 'sidebar__item--active' : ''}`}
                    onClick={onNavigate}
                >
                    <span className="sidebar__icon" aria-hidden="true">🚫</span>
                    Blocked Leads
                </NavLink>
            )}

            {showCanopy && (
                <NavLink
                    to="/canopy"
                    className={({ isActive }) => `sidebar__item ${isActive ? 'sidebar__item--active' : ''}`}
                    onClick={onNavigate}
                >
                    <span className="sidebar__icon" aria-hidden="true">🌳</span>
                    Canopy Sync
                </NavLink>
            )}

            {showCommissions && (<>
                <button
                    type="button"
                    className={`sidebar__item sidebar__parent ${onCommissionsRoute ? 'sidebar__item--active' : ''}`}
                    onClick={() => setCommissionsOpen(open => !open)}
                    aria-expanded={commissionsOpen}
                >
                    <span className="sidebar__icon" aria-hidden="true">💵</span>
                    Commissions
                    <span className={`sidebar__chevron ${commissionsOpen ? 'sidebar__chevron--open' : ''}`}>▸</span>
                </button>

                {commissionsOpen && (
                    <div className="sidebar__submenu">
                        {COMMISSION_PAGES.map(page => (
                            <NavLink
                                key={page.to}
                                to={page.to}
                                className={({ isActive }) => `sidebar__subitem ${isActive ? 'sidebar__subitem--active' : ''}`}
                                title={page.description}
                                onClick={onNavigate}
                            >
                                {page.label}
                            </NavLink>
                        ))}
                    </div>
                )}
            </>)}

            {showAdmin && (<>
                <button
                    type="button"
                    className={`sidebar__item sidebar__parent ${onAdminRoute ? 'sidebar__item--active' : ''}`}
                    onClick={() => setAdminOpen(open => !open)}
                    aria-expanded={adminOpen}
                >
                    <span className="sidebar__icon" aria-hidden="true">👥</span>
                    Users
                    <span className={`sidebar__chevron ${adminOpen ? 'sidebar__chevron--open' : ''}`}>▸</span>
                </button>

                {adminOpen && (
                    <div className="sidebar__submenu">
                        {ADMIN_PAGES.map(page => (
                            <NavLink
                                key={page.to}
                                to={page.to}
                                className={({ isActive }) => `sidebar__subitem ${isActive ? 'sidebar__subitem--active' : ''}`}
                                title={page.description}
                                onClick={onNavigate}
                            >
                                {page.label}
                            </NavLink>
                        ))}
                    </div>
                )}
            </>)}
        </nav>
    );
}
